import { Injectable } from '@angular/core';
import { StorageService } from '../storage/storage.service';
import { environment } from '../../../environments/environment';
import { BehaviorSubject } from 'rxjs';
import { FrontendServices } from '@ibexfusion/core-utils';
import { Models } from '@ibexfusion/models';
import { SnackbarService } from '@services/snackbar/snackbar.service';

@Injectable()
export class MessengerService {

  basicMessengerService: FrontendServices.BasicMessengerService;
  $activeProfile = new BehaviorSubject<Models.Profile>(null);
  $activeSession = new BehaviorSubject(null);
  $activeOrganisation = new BehaviorSubject<Models.MyOrganisation>(null);
  $activePrivileges = new BehaviorSubject<Models.MyPrivilege[]>(null);
  $superadminPrivileges = new BehaviorSubject<Models.MyPrivilege[]>(null);
  $activeRoles = new BehaviorSubject<Models.Role[]>(null);
  $activeContents = new BehaviorSubject<Models.GroupHasContent[]>(null);
  $resetPasswordData = new BehaviorSubject<any>(null);

  $dataForDashboard = new BehaviorSubject<any>(null);
  $routeFromDashboard = new BehaviorSubject<any>(null);

  $packageConfiguration = new BehaviorSubject<any>(null);

  $logout = new BehaviorSubject<any>(null);

  private usedApi;
  private apiUrl;

  private initCounter = 0;

  constructor(storage: StorageService, private ss: StorageService, private snackbar: SnackbarService) {
    this.setActiveApi();
    this.basicMessengerService = new FrontendServices.BasicMessengerService(storage, this.apiUrl, environment.serverPublicKey, 'ibexfusion');
    this.$activeOrganisation = <any>this.basicMessengerService.$activeOrganisation;
    this.$activeSession = <any>this.basicMessengerService.$activeSession;
    this.$activeSession.subscribe(session => {
      if (!session || !session.profile) {
        this.$activeProfile.next(null);
        return;
      }
      const profile = new Models.Profile();
      profile.populateFromJSON(session.profile);
      this.$activeProfile.next(profile);
    });

    this.$activeOrganisation.subscribe(organisation => {
      this.initOrganisation(organisation);
    });
  }

  async initOrganisation(organisation) {
    this.$activePrivileges.next(null);
    this.$activeRoles.next(null);
    if (!organisation) {
      return;
    }
    this.$activePrivileges.next((await this.find('my-privileges', { OrganisationID: organisation.OrganisationID })).data);
    this.$superadminPrivileges.next((await this.find('my-privileges', { privilegeName: 'superadmin' })).data);
    const groups = (await this.find('group-has-profiles', { ProfileID: this.$activeProfile.getValue().ProfileID, $limit: 100, $skip: 0 })).data;
    let contents = [];
    if (groups?.length) {
      const ids = groups.map(el => el.GroupID);
      if (ids.length) {
        const roles = (await this.find('group-has-role-in-organisations', { OrganisationID: organisation.OrganisationID, GroupID: ids, $limit: 100, $skip: 0 })).data;
        this.$activeRoles.next(roles);
        for (const role of (roles || []).filter(el => el.roleName === 'contentAuthor')) {
          contents = ((await this.find('group-has-contents', { Groups_GroupID: role.GroupID, $limit: 100, $skip: 0 })).data || []).concat(contents);
        }
      }
    }
    this.$activeContents.next(contents);
  }

  async initialize(): Promise<any> {
    this.initCounter++;
    try {
      return await this.basicMessengerService.initialize();
    } catch {
      const token = await this.ss.get('rememberToken');
      if (token) {
        await this.ss.remove('rememberToken');
        return this.initialize();
      }
      if (this.initCounter === 5) {
        await this.ss.clear();
        await this.ss.removeAllData();
        return this.initialize();
      }
      return new Promise(resolve => setTimeout(() => resolve(this.initialize()), 1000));
    }
  }


  setDataForDashboard(data) {
    this.$dataForDashboard.next(data);
  }

  setRouteFromDashboard(data) {
    this.$routeFromDashboard.next(data);
  }

  setPackageConfiguration(data) {
    this.$packageConfiguration.next(data);
  }

  getSessionLevel() {
    if (this.basicMessengerService.$activeSession.getValue()) {
      return this.basicMessengerService.$activeSession.getValue().level;
    }
    return -1;
  }

  getOrganisation() {
    if (this.$activeOrganisation.getValue()) {
      return this.$activeOrganisation.getValue();
    }
    return null;
  }

  checkContent(id): Boolean {
    if (this.$activeContents.getValue()?.length) {
      const ids = this.$activeContents.getValue().map(el => el.dynamicContentEntrance_Id);
      return ids.indexOf(id) > -1;
    } else {
      return true;
    }
  }

  getActiveAPI(): string {
    return this.usedApi;
  }

  setActiveApi() {
    this.apiUrl = environment.apiBaseURL;
    if (this.apiUrl === environment.live.apiBaseURL) {
      this.usedApi = 'live';
    } else if (this.apiUrl === environment.staging.apiBaseURL) {
      this.usedApi = 'staging';
    } else if (this.apiUrl.indexOf('localhost') > -1) {
      this.usedApi = 'local';
    }
    const apiSettings = JSON.parse(localStorage.getItem('api-settings'));
    if (environment.changeApi && apiSettings) {
      this.usedApi = apiSettings.usedApi;
      this.apiUrl = apiSettings.apiUrl;
    }
  }

  changeActiveApi(env: string, reload?: boolean) {
    if (!environment.changeApi) {
      return;
    }
    if (this.usedApi !== env) {
      window.localStorage.clear();
    }
    const apiSettingsMap = { 'staging': { usedApi: 'staging', apiUrl: environment.staging.apiBaseURL }, 'live': { usedApi: 'live', apiUrl: environment.live.apiBaseURL }, };
    if (env === 'local') {
      localStorage.removeItem('api-settings');
    } else if (apiSettingsMap[env]) {
      localStorage.setItem('api-settings', JSON.stringify(apiSettingsMap[env]));
    }
    if (reload) {
      window.location.reload();
    }
  }

  setOrganisation(organisation: Models.MyOrganisation) {
    this.$activeOrganisation.next(organisation);
  }

  async loginWithEmailPasswordCredential(loginName: string, password: string) {
    return await this.basicMessengerService.loginWithEmailPasswordCredential(loginName, password);
  }

  logout(): Promise<any> {
    this.$logout.next(true);
    return this.basicMessengerService.logout();
  }

  createEmailPasswordCredential(loginName: string, password: string): Promise<any> {
    return this.basicMessengerService.createEmailPasswordCredential(loginName, password);
  }

  createDeviceIdTokenCredential(): Promise<any> {
    return this.basicMessengerService.createDeviceIdTokenCredential();
  }

  createPersonalGroup(ProfileID: string) {
    return this.basicMessengerService.create('personalGroups', { ProfileID: ProfileID });
  }

  // password reset Services
  createResetCredentials(loginName: string, activation = false): Promise<any> {
    return this.basicMessengerService.createResetCredentials(loginName, activation);
  }

  activateAccount(data: any, key: string) {
    return this.basicMessengerService.activateAccount(data, key).then(result => {
      return result;
    }, err => {
      return err;
    });
  }

  validateResetToken(accessToken: string): Promise<any> {
    return this.basicMessengerService.validateResetToken({ accessToken: accessToken });
  }

  setResetPasswordData(resetPasswordData: any) {
    this.$resetPasswordData.next(resetPasswordData);
  }

  getResetPasswordData() {
    if (this.$resetPasswordData.getValue()) {
      return this.$resetPasswordData.getValue();
    }
    return null;
  }

  resetPasswordWithToken(loginName: string, password: string, resetAccountID: string, type: string): Promise<any> {
    return this.basicMessengerService.resetPasswordWithToken(loginName, password, resetAccountID, type);
  }

  updateCredentials(AccountId: string, data: any): Promise<any> {
    return this.basicMessengerService.updateCredentials(AccountId, data);
  }

  public find(name: string, params?: any): Promise<any> {
    return this.basicMessengerService.find(name, params);
  }

  public get(name: string, id: string, params?: any): Promise<any> {
    return this.basicMessengerService.get(name, id, params);
  }

  private handleRequest(method: 'create' | 'update' | 'patch' | 'remove', name: string, dataOrId: any, data?: any, params?: any): Promise<any> {
    this.showLoadingOverlay(true);
    return this.basicMessengerService[method](name, dataOrId, data, params).then(
      (result) => {
        this.showLoadingOverlay(false);
        if (!!result?.errors?.length) {
          this.displayErr(method, { error: result });
          return Promise.reject({ error: result });
        }
        return result;
      },
      (err) => {
        this.showLoadingOverlay(false);
        this.displayErr(method, err);
        return Promise.reject(err);
      }
    );
  }

  public create(name: string, data: any, params?: any): Promise<any> {
    return this.handleRequest('create', name, data, undefined, params);
  }

  public update(name: string, id: string, data: any, params?: any): Promise<any> {
    return this.handleRequest('update', name, id, data, params);
  }

  public patch(name: string, id: string, data: any, params?: any): Promise<any> {
    return this.handleRequest('patch', name, id, data, params);
  }

  public remove(name: string, id: string, params?: any): Promise<any> {
    return this.handleRequest('remove', name, id, undefined, params);
  }

  public showLoadingOverlay(show: boolean) {
    const overlay = document.querySelector('.loading-overlay');
    if (overlay) {
      overlay.classList.toggle('show', show);
    }
  }

  public displayErr(type, err) {
    if (type === 'routing' || (err && err.error && err.error.message && err.error.message.startsWith('insufficient permission'))) {
      this.snackbar.open(type + ' - Aktion nicht erlaubt: Keine Berechtigung ', 5000);
      return true;
    }
    return false;
  }
}
